import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Box,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { districtService } from '../../services/districtService';

const AddressSelector = ({
  selectedDistrict = '',
  selectedSubdivision = '',
  selectedBlock = '',
  selectedGPU = '',
  selectedWard = '',
  onDistrictChange,
  onSubdivisionChange,
  onBlockChange,
  onGPUChange,
  onWardChange,
  showDistrict = true,
  showSubdivision = true,
  showBlock = true,
  showGPU = true,
  showWard = true,
  required = false,
  disabled = false,
  size = 'medium',
  variant = 'outlined',
  fullWidth = true,
}) => {
  // Fetch districts
  const { data: districts = [], isLoading: loadingDistricts } = useQuery({
    queryKey: ['districts'],
    queryFn: districtService.getDistricts,
    enabled: showDistrict,
  });

  // Fetch subdivisions
  const { data: subdivisions = [], isLoading: loadingSubdivisions } = useQuery({
    queryKey: ['subdivisions', selectedDistrict],
    queryFn: () => selectedDistrict ? districtService.getSubdivisions('id', selectedDistrict) : Promise.resolve([]),
    enabled: showSubdivision && !!selectedDistrict,
  });

  // Fetch blocks
  const { data: blocks = [], isLoading: loadingBlocks } = useQuery({
    queryKey: ['blocks', selectedSubdivision],
    queryFn: () => selectedSubdivision ? districtService.getBlocks(selectedSubdivision) : Promise.resolve([]),
    enabled: showBlock && !!selectedSubdivision,
  });

  // Fetch GPUs
  const { data: gpus = [], isLoading: loadingGPUs } = useQuery({
    queryKey: ['gpus', selectedBlock],
    queryFn: () => selectedBlock ? districtService.getGPUs(selectedBlock) : Promise.resolve([]),
    enabled: showGPU && !!selectedBlock,
  });

  // Fetch wards
  const { data: wards = [], isLoading: loadingWards } = useQuery({
    queryKey: ['wards', selectedGPU],
    queryFn: () => selectedGPU ? districtService.getWards(selectedGPU) : Promise.resolve([]),
    enabled: showWard && !!selectedGPU,
  });

  const handleDistrictChange = (value) => {
    if (onDistrictChange) onDistrictChange(value);
    // Reset dependent fields
    if (onSubdivisionChange) onSubdivisionChange('');
    if (onBlockChange) onBlockChange('');
    if (onGPUChange) onGPUChange('');
    if (onWardChange) onWardChange('');
  };

  const handleSubdivisionChange = (value) => {
    if (onSubdivisionChange) onSubdivisionChange(value);
    // Reset dependent fields
    if (onBlockChange) onBlockChange('');
    if (onGPUChange) onGPUChange('');
    if (onWardChange) onWardChange('');
  };

  const handleBlockChange = (value) => {
    if (onBlockChange) onBlockChange(value);
    // Reset dependent fields
    if (onGPUChange) onGPUChange('');
    if (onWardChange) onWardChange('');
  };

  const handleGPUChange = (value) => {
    if (onGPUChange) onGPUChange(value);
    // Reset dependent fields
    if (onWardChange) onWardChange('');
  };

  const renderSelect = (label, value, onChange, options, loading, show, enabledCondition = true) => {
    if (!show) return null;

    return (
      <FormControl fullWidth={fullWidth} size={size} variant={variant} required={required} disabled={disabled || !enabledCondition}>
        <InputLabel>{label}{required ? ' *' : ''}</InputLabel>
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          label={`${label}${required ? ' *' : ''}`}
          disabled={disabled || !enabledCondition}
        >
          <MenuItem value="">
            <em>Select {label}</em>
          </MenuItem>
          {loading ? (
            <MenuItem disabled>
              <Box display="flex" alignItems="center">
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Loading...
              </Box>
            </MenuItem>
          ) : (
            options.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name} {option.code && `(${option.code})`}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    );
  };

  return (
    <Grid container spacing={2}>
      {showDistrict && (
        <Grid size={{ xs: 12, sm: showSubdivision || showBlock || showGPU || showWard ? 6 : 12, md: showSubdivision && showBlock && showGPU && showWard ? 2.4 : 12 / [showDistrict, showSubdivision, showBlock, showGPU, showWard].filter(Boolean).length }} >
          {renderSelect('District', selectedDistrict, handleDistrictChange, districts, loadingDistricts, showDistrict)}
        </Grid>
      )}
      
      {showSubdivision && (
        <Grid size={{ xs: 12, sm: 6, md: showDistrict && showBlock && showGPU && showWard ? 2.4 : 12 / [showDistrict, showSubdivision, showBlock, showGPU, showWard].filter(Boolean).length }} >
          {renderSelect('Subdivision', selectedSubdivision, handleSubdivisionChange, subdivisions, loadingSubdivisions, showSubdivision, !!selectedDistrict)}
        </Grid>
      )}
      
      {showBlock && (
        <Grid size={{ xs: 12, sm: 6, md: showDistrict && showSubdivision && showGPU && showWard ? 2.4 : 12 / [showDistrict, showSubdivision, showBlock, showGPU, showWard].filter(Boolean).length }} >
          {renderSelect('Block', selectedBlock, handleBlockChange, blocks, loadingBlocks, showBlock, !!selectedSubdivision)}
        </Grid>
      )}
      
      {showGPU && (
        <Grid size={{ xs: 12, sm: 6, md: showDistrict && showSubdivision && showBlock && showWard ? 2.4 : 12 / [showDistrict, showSubdivision, showBlock, showGPU, showWard].filter(Boolean).length }}>
          {renderSelect('GPU', selectedGPU, handleGPUChange, gpus, loadingGPUs, showGPU, !!selectedBlock)}
        </Grid>
      )}
      
      {showWard && (
        <Grid size={{ xs: 12, sm: 6, md: showDistrict && showSubdivision && showBlock && showGPU ? 2.4 : 12 / [showDistrict, showSubdivision, showBlock, showGPU, showWard].filter(Boolean).length }} >
          {renderSelect('Ward', selectedWard, onWardChange, wards, loadingWards, showWard, !!selectedGPU)}
        </Grid>
      )}
    </Grid>
  );
};

export default AddressSelector;
